import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ShieldCheck, Plus, Loader2, Users, Trash2, Pencil, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nom: '', description: '', permissions: {} });

  const fetchAll = async () => {
    try {
      const [rRes, mRes] = await Promise.all([
        axios.get(`${API_URL}/roles`),
        axios.get(`${API_URL}/roles/modules`),
      ]);
      setRoles(rRes.data);
      setModules(mRes.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    // Init empty permissions map
    const perms = {};
    modules.forEach(m => { perms[m.code] = []; });
    setForm({ nom: '', description: '', permissions: perms });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    if (role.is_system) return;
    setEditing(role.id);
    const perms = {};
    modules.forEach(m => { perms[m.code] = []; });
    (role.permissions || []).forEach(rp => { perms[rp.module] = rp.permissions || []; });
    setForm({ nom: role.nom, description: role.description || '', permissions: perms });
    setDialogOpen(true);
  };

  const togglePerm = (module, perm) => {
    setForm(prev => {
      const current = prev.permissions[module] || [];
      const updated = current.includes(perm) ? current.filter(p => p !== perm) : [...current, perm];
      return { ...prev, permissions: { ...prev.permissions, [module]: updated } };
    });
  };

  const toggleAllModule = (module, allPerms) => {
    setForm(prev => {
      const current = prev.permissions[module] || [];
      const allSelected = allPerms.every(p => current.includes(p.code));
      return { ...prev, permissions: { ...prev.permissions, [module]: allSelected ? [] : allPerms.map(p => p.code) } };
    });
  };

  const handleSave = async () => {
    if (!form.nom) { toast.error('Nom du rôle requis'); return; }
    setSaving(true);
    try {
      const permissions = Object.entries(form.permissions)
        .filter(([_, perms]) => perms.length > 0)
        .map(([module, perms]) => ({ module, permissions: perms }));

      if (editing) {
        await axios.put(`${API_URL}/roles/${editing}`, { nom: form.nom, description: form.description, permissions });
        toast.success('Rôle modifié');
      } else {
        await axios.post(`${API_URL}/roles`, { nom: form.nom, description: form.description, permissions });
        toast.success('Rôle créé');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (roleId) => {
    if (!confirm('Supprimer ce rôle ?')) return;
    try {
      await axios.delete(`${API_URL}/roles/${roleId}`);
      toast.success('Rôle supprimé');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const countPerms = (role) => {
    return (role.permissions || []).reduce((s, rp) => s + (rp.permissions?.length || 0), 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            <ShieldCheck className="w-6 h-6 inline mr-2" />Rôles & Permissions
          </h1>
          <p className="text-slate-500">{roles.length} rôle(s) configuré(s)</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0F5156] hover:bg-[#0d464a]">
          <Plus className="w-4 h-4 mr-2" /> Nouveau rôle
        </Button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <div className="grid gap-4">
          {roles.map(role => (
            <Card key={role.id} className={`${role.is_system ? 'border-slate-200' : 'border-[#0F5156]/20'}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.is_system ? 'bg-slate-100' : 'bg-[#0F5156]/10'}`}>
                      {role.is_system ? <Lock className="w-5 h-5 text-slate-500" /> : <ShieldCheck className="w-5 h-5 text-[#0F5156]" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{role.nom}</h3>
                        <Badge variant="outline" className="text-xs font-mono">{role.code}</Badge>
                        {role.is_system && <Badge className="bg-blue-100 text-blue-700 text-xs">Système</Badge>}
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{role.description || 'Aucune description'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <div className="flex items-center gap-1 text-slate-500"><Users className="w-3.5 h-3.5" /> {role.user_count || 0} utilisateur(s)</div>
                      <div className="text-slate-400">{countPerms(role)} permission(s)</div>
                    </div>
                    {!role.is_system && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(role)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(role.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    )}
                  </div>
                </div>
                {/* Permissions summary */}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(role.permissions || []).map(rp => (
                    rp.permissions?.length > 0 && (
                      <Badge key={rp.module} variant="outline" className="text-xs">
                        {modules.find(m => m.code === rp.module)?.label || rp.module}: {rp.permissions.length}
                      </Badge>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le rôle' : 'Nouveau rôle personnalisé'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nom du rôle *</Label><Input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} placeholder="ex: Responsable achats" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description courte" /></div>
            </div>

            <Separator />

            <div>
              <Label className="text-base font-semibold">Permissions par module</Label>
              <p className="text-sm text-slate-500 mb-4">Cochez les permissions accordées à ce rôle pour chaque module.</p>

              <div className="space-y-4">
                {modules.map(mod => {
                  const modPerms = form.permissions[mod.code] || [];
                  const allSelected = mod.permissions.length > 0 && mod.permissions.every(p => modPerms.includes(p.code));
                  const someSelected = mod.permissions.some(p => modPerms.includes(p.code));

                  return (
                    <Card key={mod.code} className={`${!mod.available_in_subscription ? 'opacity-50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={allSelected}
                              onCheckedChange={() => toggleAllModule(mod.code, mod.permissions)}
                              disabled={!mod.available_in_subscription}
                            />
                            <span className="font-medium text-sm">{mod.label}</span>
                            {!mod.available_in_subscription && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Hors abonnement</Badge>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">{modPerms.length}/{mod.permissions.length}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {mod.permissions.map(perm => (
                            <button
                              key={perm.code}
                              type="button"
                              disabled={!mod.available_in_subscription}
                              onClick={() => togglePerm(mod.code, perm.code)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                modPerms.includes(perm.code)
                                  ? 'bg-[#0F5156] text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                              }`}
                            >
                              {perm.label}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Enregistrer' : 'Créer le rôle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
